import React, { useState } from 'react'
import { DayPickerRangeController } from 'react-dates'
import classnames from 'classnames'
import type { Moment } from 'moment'
import moment from 'moment'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import IconButton from '../IconButton'
import DatePickerMonthDropdown from './DatePickerMonthDropdown'
import { useDatePickerStyles } from './useDatePickerStyles'

export interface DateRangePickerControllerProps {
  startDate?: Moment | null
  endDate?: Moment | null
  onChange: (startDate: Moment | null, endDate: Moment | null) => void
  recalculateTableHeight?: () => void
  blockPastDays?: boolean
  initialVisibleMonth?: () => Moment | null
  minimumNights?: number
}

export default function DateRangePickerController(props: DateRangePickerControllerProps) {
  const { startDate, endDate, recalculateTableHeight, onChange, blockPastDays, initialVisibleMonth, minimumNights } =
    props
  const datePickerClasses = useDatePickerStyles({ isSingle: true, blockPastDays, startDate, endDate })

  const [focusedInput, setFocusedInput] = useState<'startDate' | 'endDate'>('startDate')

  const recalculateTableHeightWithDelay = () => {
    if (recalculateTableHeight) {
      setTimeout(() => recalculateTableHeight(), 1)
    }
  }

  return (
    <DayPickerRangeController
      minimumNights={minimumNights}
      transitionDuration={0}
      startDate={startDate || null}
      endDate={endDate || null}
      onDatesChange={({ startDate, endDate }) => onChange(startDate, endDate)}
      focusedInput={focusedInput}
      onFocusChange={(input) => setFocusedInput(input || 'startDate')}
      daySize={45}
      noBorder
      initialVisibleMonth={initialVisibleMonth ? initialVisibleMonth : () => startDate || moment()}
      navPrev={
        <IconButton className={datePickerClasses.navButton} size="small">
          <ChevronLeftIcon />
        </IconButton>
      }
      navNext={
        <IconButton className={classnames(datePickerClasses.navButton, datePickerClasses.rightNavButton)} size="small">
          <ChevronRightIcon />
        </IconButton>
      }
      onPrevMonthClick={recalculateTableHeight}
      onNextMonthClick={recalculateTableHeight}
      renderMonthElement={(args) => (
        <DatePickerMonthDropdown
          {...args}
          onYearSelect={(currentMonth, newMonth) => {
            args.onYearSelect(currentMonth, newMonth)
            recalculateTableHeightWithDelay()
          }}
          onMonthSelect={(currentMonth, newMonth) => {
            args.onMonthSelect(currentMonth, newMonth)
            recalculateTableHeightWithDelay()
          }}
        />
      )}
      enableOutsideDays
      isDayBlocked={(day) => {
        return blockPastDays ? day.isBefore(moment(), 'day') : false
      }}
    />
  )
}
